<template>
    <div class="collection-host">
        <!-- Above the layout, not below it. The layout fills the height, so a
             sibling after it lands past the fold — this banner used to render
             at y=2119 in a 950-pixel viewport, which is a message nobody could
             ever read. -->
        <Message v-if="failure" severity="error" :closable="false" class="collection-host__error">
            {{ failureText(failure) }}
        </Message>

        <!-- Not before the surface arrives. The component validates its
             `fieldDefinitions` on mount and FQL refuses an empty set outright —
             `FIELD_DEFINITIONS_INVALID: fields must declare at least one field`
             — so rendering it against the null the store starts with is a
             thrown error rather than an empty screen. -->
        <AdminCollectionLayout
            v-if="store.surface"
            class="collection-host__layout"
            :config="config"
            controlled
            search-ui="bar"
            :simplify-when-small="false"
            :initial-query="initialQuery"
            :value-provider="valueProvider"
            :facet-provider="facetProvider"
            @update:query="onQuery"
            @update:sort="onSort"
        >
            <template
                #instance="{ record, draft, isNew, editing, setField, fieldDefinitions, fieldMeta }"
            >
                <AdministrationInstanceBody
                    :record="record"
                    :draft="draft"
                    :collection="collection"
                    :store="store"
                    :is-new="isNew"
                    :editing="editing"
                    :needs-operation="needsOperation"
                    :editable-fields="editable"
                    :editable-objects="editableObjects"
                    :field-definitions="fieldDefinitions"
                    :field-meta="fieldMeta"
                    :set-field="setField"
                    :write-failure="writeFailure"
                />
            </template>
        </AdminCollectionLayout>

        <!-- The layout renders whatever it is handed and paginates nothing, so
             reaching page two is this screen's job. Hidden on a collection that
             fits in one page, where a paginator would be one row of chrome
             saying "1 of 1". -->
        <Paginator
            v-if="store.surface && store.totalCount > pageSize"
            class="collection-host__paginator"
            :rows="pageSize"
            :total-records="store.totalCount"
            :first="(page - 1) * pageSize"
            @page="(event) => goToPage(event.page + 1)"
        />

        <!-- Its own condition, not an `v-else-if` on the paginator above. -->
        <ProgressSpinner v-if="!store.surface && store.loading" class="collection-host__loading" />
    </div>
</template>

<script setup>
import {
    useAuctionHousesStore,
    useAuctionOperationsStore,
    useCompaniesStore,
    useDealershipOperationsStore,
    useDealershipsStore
} from '@hds/administration-client-sdk'
import Message from 'primevue/message'
import Paginator from 'primevue/paginator'
import ProgressSpinner from 'primevue/progressspinner'
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import AdminCollectionLayout from '@/admin-collection-layout/components/AdminCollectionLayout.vue'
import { failureText } from '@/lib/failureText.js'
import AdministrationInstanceBody from '@/components/AdministrationInstanceBody.vue'
import { useAdministrationCollection } from '@/composables/useAdministrationCollection.js'

// One view for all five nouns. They are one screen at this layer: the same
// search, the same list, the same detail panel over a different collection.
//
// Setup runs once per collection rather than once for the app, because App.vue
// keys the router outlet on the route. That is what lets everything below be
// plain — no watch on the prop, no store held in a computed, no remount key.
const props = defineProps({
    collection: { type: String, required: true },
    title: { type: String, required: true },
    singular: { type: String, required: true }
})

// The SDK ships a store per collection rather than one parameterised by name,
// so this is where a route id becomes a store. Five entries beats a factory
// nobody else would call.
const STORES = {
    'companies': useCompaniesStore,
    'auction-operations': useAuctionOperationsStore,
    'dealership-operations': useDealershipOperationsStore,
    'auction-houses': useAuctionHousesStore,
    'dealerships': useDealershipsStore
}

const store = STORES[props.collection]()
const {
    config,
    onQuery,
    onSort,
    loadSurface,
    readFailure,
    writeFailure,
    page,
    pageSize,
    goToPage,
    valueProvider,
    facetProvider,
    editable,
    editableObjects,
    needsOperation
} = useAdministrationCollection({
    store,
    collection: props.collection,
    title: props.title,
    singular: props.singular,
    storageKey: props.collection
})

/**
 * A filter carried in by a link, as FQL.
 *
 * `?q=operationInfo.operationId:…` is how an Operation's roster points at the
 * houses it named. Read once rather than watched: App.vue keys the outlet on
 * the full path, so a second link into this collection is a fresh view rather
 * than this one being handed a new query.
 *
 * A repeated parameter arrives as an array, which is not a query — that reads
 * as nothing rather than as `[object Object]` in the search box.
 */
const route = useRoute()

const initialQuery = computed(function () {
    const asked = route.query.q
    if (typeof asked !== 'string') {
        return ''
    }
    return asked
})

// Only the surface. The layout fires `@update:query` as soon as it mounts —
// with whatever query it had persisted, empty on a first visit — and that reads
// the first page through `search`. So listing here made a second request for the
// same rows and left the store believing a `list` had produced them, which is
// the wrong read for `refresh` to repeat.
onMounted(loadSurface)

/**
 * What the screen says, which is not everything the store caught.
 *
 * Only the reads. Every write on this screen reports beside the control that
 * made it — the status buttons, each relationship row, each list editor — and
 * `store.error` holds writes and reads alike, so a banner built on it printed
 * every refusal twice, the second time far from what it was about.
 *
 * A rejected query is left to the search bar even so. It holds the same FQL
 * engine the server converts with, so it has already underlined the clause and
 * named the field — `bogus:1` reads "UNKNOWN FIELD" beside the query itself. It
 * also fires on every keystroke of a clause somebody is still typing: `status:`
 * is a 400 on the way to `status:active`.
 */
const failure = computed(function () {
    if (readFailure.value === null || readFailure.value.code === 'INVALID_SEARCH_QUERY') {
        return null
    }
    return readFailure.value
})
</script>

<style scoped>
.collection-host {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.collection-host__error {
    flex: 0 0 auto;
    margin: 16px 16px 0;
}

/* The shell sets `height: 100%` on its own root, so it needs to be told it may
   shrink — otherwise it claims the full column and the paginator below it lands
   past the fold, which is where the error banner used to be. */
.collection-host__layout {
    flex: 1 1 auto;
    min-height: 0;
}

.collection-host__paginator {
    border-top: 1px solid var(--p-content-border-color);
    flex: 0 0 auto;
}

.collection-host__loading {
    align-self: center;
    margin-top: 25vh;
}
</style>
